import { routeNames } from '@/router/route-names'
import {
  fetchAuthSession as cognitoFetchAuthSession,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  confirmSignIn,
  fetchUserAttributes as cognitoFetchUserAttributes,
  updateUserAttributes as cognitoUpdateUserAttributes
} from 'aws-amplify/auth'

import { router } from '@/router'
import { setSentryUser } from '@/plugins'

export const useAuthStore = defineStore('authStore', () => {
  const user = ref<TNullable<IUser>>()
  const mfaRequired = ref(null)

  function handleSignIn ({ email, password }: ISignInInput) {
    return cognitoSignIn({ username: email.toLowerCase(), password })
      .then((res) => {
        if (res.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
          mfaRequired.value = true
          return { requiresMfa: true }
        } else if (res.isSignedIn) {
          getUserProfile()
          return { requiresMfa: false }
        }
      })
  }

  function confirmMfa (code: string) {
    return confirmSignIn({ challengeResponse: code })
      .then((res) => {
        if (res.isSignedIn) {
          mfaRequired.value = null
          return getUserProfile()
        }
      })
  }

  function getToken () {
    return cognitoFetchAuthSession()
      .then(session => session.tokens?.accessToken.toString())
  }

  function getUserProfile () {
    return Promise.all([
      cognitoFetchUserAttributes(),
      authService.getMe()
    ])
      .then(([attrs, getMeResponse]) => {
        // @ts-check important param added because _attrs.sub and _attrs.email always exist in AWS
        user.value = {
          subId: attrs.sub!,
          email: attrs.email!,
          name: attrs.name ?? '',
          impersonatedUserEmail: getMeResponse.email,
          role: getMeResponse.systemRole,
          schoolId: getMeResponse.schools?.[0]?.schoolId || ''
        }

        setSentryUser(user.value)
      })
  }

  function updateUserAttributes (input: TUserModifiable) {
    return cognitoUpdateUserAttributes({
      userAttributes: input
    }).then(() => user.value && Object.assign(user.value, input))
  }

  function handleSignOut (redirect = true) {
    return cognitoSignOut().finally(() => {
      if (redirect) {
        window.location.href = router.resolve({ name: routeNames.login }).href
      }
      cookieService.deleteCookie('impersonateToken')
    })
  }

  function $reset () {
    user.value = null
  }

  return {
    user,
    mfaRequired,

    confirmMfa,
    handleSignIn,
    handleSignOut,
    getUserProfile,
    getToken,
    updateUserAttributes,

    $reset
  }
})
